import React from "react";
import { Trans } from "@lingui/macro";
import { Loader } from "../../../../UI/Loader/Loader";
import { Button } from "../../../../UI/Button/Button";
import { useDispatch, useSelector } from "react-redux";
import { InputText } from "../../../../UI/InputText/InputText";
import { useInputChange } from "../../../../../hooks/useInputChange";
import { getStoreList } from "../../../../../model/dashboardStores/dStoresAPI";
import { logoutSagaAC } from "../../../../../model/system/systemActionCreators";
import { selectProductsFilters } from "../../../../../model/dashboardProducts/selectors";
import { typeProductsFilters } from "../../../../../model/dashboardProducts/dProductsReducer";
import { dProductsSetFiltersAC } from "../../../../../model/dashboardProducts/dProductsActionCreators";
import { getNotDeletedCategoriesList } from "../../../../../model/dashboardCategories/dCategoriesAPI";
import {
  NULL_VALUE,
  SelectBox,
  typeValuesListItem,
} from "../../../../UI/SelectBox/SelectBox";

export type typeDProductsFilters = {
  setOpenFilterBar: React.Dispatch<React.SetStateAction<boolean>>;
  setIsActiveFilters: React.Dispatch<React.SetStateAction<boolean>>;
};

export const DProductsFilters: React.FC<typeDProductsFilters> = ({
  setOpenFilterBar,
  setIsActiveFilters,
}) => {
  const dispatch = useDispatch();

  const filters = useSelector(selectProductsFilters);

  React.useEffect(() => {
    setLoaderStore(true);
    setLoaderCategory(true);
    const setData = async () => {
      let responseCategories = await getNotDeletedCategoriesList();

      if (responseCategories.status === 200) {
        const newCategoriesList = [
          ...selectCategoryList,
          ...responseCategories.data,
        ];
        setSelectCategoryList(newCategoriesList);
      } else if (responseCategories.status === 401) {
        dispatch(logoutSagaAC());
      }

      let responseStores = await getStoreList({
        filters: { name: null },
        full: false,
      });

      if (responseStores.status === 200) {
        const notDeletedStores = responseStores.data.filter(
          (s: any) => !s.deleted,
        );

        setSelectStoreList(notDeletedStores || []);
      } else if (responseStores.status === 401) {
        dispatch(logoutSagaAC());
      }
      setLoaderStore(false);
      setLoaderCategory(false);
    };

    setData().catch(console.error);
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    if (filters.store) {
      setStoreIdValue(filters.store);
    }
  }, [filters]);

  const [storeIdValue, setStoreIdValue] = React.useState<string>(
    () => filters.store || "",
  );
  const [selectStoreList, setSelectStoreList] = React.useState<
    typeValuesListItem[]
  >([]);

  const [loaderStore, setLoaderStore] = React.useState(false);

  const {
    value: barcodeValue,
    setValue: setBarcodeValue,
    changeValueHandler: onChangeBarcodeValue,
  } = useInputChange(filters.barcode ? filters.barcode : "");

  const OUT_OF_CATEGORIES = "outOfCategories";
  const [categoryIdValue, setCategoryIdValue] = React.useState<string | null>(
    filters.category!,
  );
  const [selectCategoryList, setSelectCategoryList] = React.useState<
    typeValuesListItem[]
  >([
    {
      id: OUT_OF_CATEGORIES,
      name: <Trans>Out of categories</Trans>,
    },
  ]);
  const [loaderCategory, setLoaderCategory] = React.useState(false);

  React.useEffect(() => {
    if ("category" in filters) {
      setCategoryIdValue(
        filters.category === OUT_OF_CATEGORIES
          ? OUT_OF_CATEGORIES
          : filters.category!,
      );
    } else {
      setCategoryIdValue(NULL_VALUE.id);
    }
  }, [filters]);

  const BY_PIECE = "byPiece";
  const NOT_BY_PIECE = "notByPiece";
  const IntegerUnitValueList: typeValuesListItem[] = [
    { id: BY_PIECE, name: <Trans>By piece</Trans> },
    { id: NOT_BY_PIECE, name: <Trans>Not by piece</Trans> },
  ];
  const [integerUnitValue, setIntegerUnitValue] = React.useState<string>(
    filters.integerUnit === null
      ? NULL_VALUE.id
      : filters.integerUnit
        ? BY_PIECE
        : NOT_BY_PIECE,
  );

  const MARKED = "marked";
  const NOT_MARKED = "notMarked";
  const markedValueList: typeValuesListItem[] = [
    { id: MARKED, name: <Trans>Marked</Trans> },
    { id: NOT_MARKED, name: <Trans>Not marked</Trans> },
  ];
  const [markedValue, setMarkedValue] = React.useState<string>(
    filters.marked === null
      ? NULL_VALUE.id
      : filters.marked
        ? MARKED
        : NOT_MARKED,
  );

  const DELETED = "deleted";
  const NOT_DELETED = "notDeleted";
  const deletedValueList: typeValuesListItem[] = [
    { id: NOT_DELETED, name: <Trans>Not deleted</Trans> },
    { id: DELETED, name: <Trans>Deleted</Trans> },
  ];
  const [deletedValue, setDeletedValue] = React.useState<string | null>(
    filters.deleted === null
      ? NULL_VALUE.id
      : filters.deleted
        ? DELETED
        : NOT_DELETED,
  );

  const ALL = "all";
  const NOT_VALID = "notValid";
  const invalidPsidValueList: typeValuesListItem[] = [
    { id: ALL, name: <Trans>All products</Trans> },
    { id: NOT_VALID, name: <Trans>With invalid PSID</Trans> },
  ];

  const [invalidPsidValue, setInvalidPsidValue] = React.useState<string>(
    filters.invalidPsid === null ? ALL : NOT_VALID,
  );

  const checkIsChangedFiltersHandler = (
    newFilters: typeProductsFilters,
  ): boolean => {
    return !(
      newFilters.integerUnit === filters.integerUnit &&
      newFilters.marked === filters.marked &&
      newFilters.deleted === filters.deleted &&
      newFilters.category === filters.category &&
      newFilters.barcode === filters.barcode &&
      newFilters.store === filters.store &&
      newFilters.invalidPsid === filters.invalidPsid
    );
  };

  const onSubmitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newFilters: typeProductsFilters = {
      integerUnit:
        integerUnitValue === BY_PIECE
          ? true
          : integerUnitValue === NOT_BY_PIECE
            ? false
            : null,
      marked:
        markedValue === MARKED
          ? true
          : markedValue === NOT_MARKED
            ? false
            : null,
      deleted:
        deletedValue === DELETED
          ? true
          : deletedValue === NOT_DELETED
            ? false
            : null,
      category:
        categoryIdValue === OUT_OF_CATEGORIES
          ? OUT_OF_CATEGORIES
          : categoryIdValue === NULL_VALUE.id
            ? undefined
            : categoryIdValue,
      barcode: barcodeValue.trim() ? barcodeValue.trim() : null,
      store: storeIdValue === NULL_VALUE.id ? null : storeIdValue,
      invalidPsid:
        invalidPsidValue === NULL_VALUE.id
          ? null
          : invalidPsidValue === NOT_VALID
            ? true
            : null,
    };

    if (checkIsChangedFiltersHandler(newFilters)) {
      dispatch(dProductsSetFiltersAC(newFilters));

      setIsActiveFilters(true);
    }

    setOpenFilterBar(false);
  };

  const onRestoreHandler = () => {
    setIntegerUnitValue(NULL_VALUE.id);
    setMarkedValue(NULL_VALUE.id);
    setDeletedValue(NOT_DELETED);
    setCategoryIdValue(NULL_VALUE.id);
    setBarcodeValue("");
    setStoreIdValue(NULL_VALUE.id);
    setInvalidPsidValue(ALL);
    setIsActiveFilters(false);
    dispatch(
      dProductsSetFiltersAC({
        integerUnit: null,
        marked: null,
        deleted: false,
        category: undefined,
        barcode: null,
        store: null,
        invalidPsid: null,
      }),
    );
  };

  return (
    <form onSubmit={(e) => onSubmitHandler(e)}>
      <div className="flex flex-col gap-4">
        <div>
          <SelectBox
            valueId={invalidPsidValue}
            setIdValue={setInvalidPsidValue}
            valuesList={invalidPsidValueList}
            label={<Trans>Invalid PSID</Trans>}
          />
        </div>
        <div className="relative">
          <SelectBox
            valueId={storeIdValue}
            labelClassname="text-left"
            label={<Trans>Store</Trans>}
            setIdValue={setStoreIdValue}
            valuesList={selectStoreList}
          />
          {loaderStore && <Loader />}
        </div>
        <div>
          <InputText
            id="barcode"
            maxLength={14}
            value={barcodeValue}
            label={<Trans>Barcode</Trans>}
            onChange={onChangeBarcodeValue}
          />
        </div>
        <div className="relative">
          <SelectBox
            valueId={categoryIdValue}
            labelClassname="text-left"
            label={<Trans>Category</Trans>}
            valuesList={selectCategoryList}
            setIdValue={setCategoryIdValue}
          />
          {loaderCategory && <Loader />}
        </div>
        <div>
          <SelectBox
            valueId={integerUnitValue}
            setIdValue={setIntegerUnitValue}
            valuesList={IntegerUnitValueList}
            label={<Trans>Sold by piece</Trans>}
          />
        </div>

        <div>
          <SelectBox
            valueId={markedValue}
            setIdValue={setMarkedValue}
            valuesList={markedValueList}
            label={<Trans>Is marked products</Trans>}
          />
        </div>

        <div>
          <SelectBox
            valueId={deletedValue}
            setIdValue={setDeletedValue}
            valuesList={deletedValueList}
            label={<Trans>Is deleted products</Trans>}
          />
        </div>

        <div className="flex gap-4 mt-4">
          <Button
            type="button"
            secondary={true}
            buttonClassName="w-full"
            onClick={onRestoreHandler}
            label={<Trans>Clear</Trans>}
          />
          <Button
            type="submit"
            buttonClassName="w-full"
            label={<Trans>Apply</Trans>}
          />
        </div>
      </div>
    </form>
  );
};
